/**
 * KnowledgePage — управление базой знаний организации.
 *
 * CRUD: список, создание, редактирование, удаление knowledge items.
 * Phase 7: Knowledge Base Management.
 */

import { useState } from 'react';
import { Button, Spinner, Card, Badge, Input } from '../components/ui';
import {
  useKnowledgeItems,
  useCreateKnowledge,
  useUpdateKnowledge,
  useDeleteKnowledge,
  type KnowledgeItem,
  type KnowledgeItemKind,
  type EmbeddingStatus,
} from '../api/knowledge';

// ============================================================================
// Form
// ============================================================================

interface KnowledgeFormProps {
  initial?: KnowledgeItem | null;
  onSave: (data: { title: string; content: string; kind: KnowledgeItemKind; category: string | null }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function KnowledgeForm({ initial, onSave, onCancel, isLoading }: KnowledgeFormProps): JSX.Element {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [content, setContent] = useState(initial?.content ?? '');
  const [kind, setKind] = useState<KnowledgeItemKind>(initial?.kind ?? 'qa');
  const [category, setCategory] = useState(initial?.category ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ title, content, kind, category: category.trim() || null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">
          {kind === 'qa' ? 'Вопрос' : 'Заголовок'}
        </label>
        <Input
          value={title}
          onChange={(v) => setTitle(v)}
          placeholder={kind === 'qa' ? 'Как оформить возврат?' : 'Название записи'}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">
          {kind === 'qa' ? 'Ответ' : 'Содержание'}
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={kind === 'qa' ? 'Для оформления возврата...' : 'Текст записи'}
          required
          rows={4}
          className="w-full px-3 py-2 bg-surface-base border border-border-default rounded-comp text-text-primary text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-brand/30 focus:border-accent-brand resize-y"
        />
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-text-primary mb-1">Тип</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as KnowledgeItemKind)}
            className="w-full px-3 py-2 bg-surface-base border border-border-default rounded-comp text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-brand/30"
          >
            <option value="qa">Вопрос-ответ</option>
            <option value="note">Заметка</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-text-primary mb-1">Категория</label>
          <Input
            value={category}
            onChange={(v) => setCategory(v)}
            placeholder="Опционально"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="primary" type="submit" disabled={!title.trim() || !content.trim() || isLoading}>
          {isLoading ? 'Сохранение...' : initial ? 'Сохранить' : 'Создать'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </form>
  );
}

// ============================================================================
// EmbeddingStatusBadge
// ============================================================================

function EmbeddingStatusBadge({ status }: { status: EmbeddingStatus }): JSX.Element {
  const map: Record<EmbeddingStatus, { label: string; variant: 'active' | 'waiting' | 'resolved' | 'ai' }> = {
    done: { label: 'Готово', variant: 'resolved' },
    pending: { label: 'Ожидание', variant: 'waiting' },
    processing: { label: 'Обработка', variant: 'ai' },
    failed: { label: 'Ошибка', variant: 'active' },
  };
  const info = map[status] ?? { label: status, variant: 'waiting' as const };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}

// ============================================================================
// KnowledgePage
// ============================================================================

export function KnowledgePage(): JSX.Element {
  const { data: items, isLoading } = useKnowledgeItems();
  const createMutation = useCreateKnowledge();
  const updateMutation = useUpdateKnowledge();
  const deleteMutation = useDeleteKnowledge();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<KnowledgeItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleCreate = (data: { title: string; content: string; kind: KnowledgeItemKind; category: string | null }) => {
    createMutation.mutate(data, {
      onSuccess: () => setShowForm(false),
    });
  };

  const handleUpdate = (data: { title: string; content: string; kind: KnowledgeItemKind; category: string | null }) => {
    if (!editing) return;
    updateMutation.mutate({ id: editing.id, ...data }, {
      onSuccess: () => setEditing(null),
    });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => setDeleteConfirm(null),
    });
  };

  const handleToggleActive = (item: KnowledgeItem) => {
    updateMutation.mutate({ id: item.id, isActive: !item.isActive });
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-base">
        <Spinner size="lg" className="text-accent-brand" />
      </div>
    );
  }

  return (
    <div className="h-full bg-surface-base overflow-auto">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-text-primary">
              База знаний
            </h1>
            <p className="text-text-secondary text-sm mt-1">
              {items?.length ?? 0} {getItemsLabel(items?.length ?? 0)}
            </p>
          </div>
          {!showForm && !editing && (
            <Button variant="primary" onClick={() => setShowForm(true)}>
              Добавить запись
            </Button>
          )}
        </div>

        {/* Create form */}
        {showForm && (
          <Card className="mb-6 p-6">
            <h2 className="font-heading text-lg font-medium text-text-primary mb-4">
              Новая запись
            </h2>
            <KnowledgeForm
              onSave={handleCreate}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </Card>
        )}

        {/* Empty state */}
        {!items?.length && !showForm && (
          <Card className="p-12 text-center">
            <div className="text-4xl mb-4">
              <svg className="w-12 h-12 mx-auto text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="font-heading text-lg font-medium text-text-primary mb-2">
              База знаний пуста
            </h3>
            <p className="text-text-secondary text-sm mb-6">
              Добавьте вопросы и ответы, чтобы AI мог отвечать на типовые обращения
            </p>
            <Button variant="primary" onClick={() => setShowForm(true)}>
              Добавить первую запись
            </Button>
          </Card>
        )}

        {/* Items list */}
        <div className="space-y-3">
          {items?.map((item) => (
            <Card key={item.id} className={`p-4 ${!item.isActive ? 'opacity-60' : ''}`}>
              {editing?.id === item.id ? (
                <KnowledgeForm
                  initial={item}
                  onSave={handleUpdate}
                  onCancel={() => setEditing(null)}
                  isLoading={updateMutation.isPending}
                />
              ) : (
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-text-primary truncate">
                          {item.title}
                        </h3>
                        <Badge variant={item.kind === 'qa' ? 'ai' : 'waiting'}>
                          {item.kind === 'qa' ? 'Q&A' : 'Заметка'}
                        </Badge>
                        <EmbeddingStatusBadge status={item.embeddingStatus} />
                        {item.category && (
                          <span className="text-xs text-text-muted bg-surface-subtle px-2 py-0.5 rounded-full">
                            {item.category}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-secondary line-clamp-2">
                        {item.content}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleToggleActive(item)}
                        className={`p-1.5 rounded-comp text-sm transition-colors ${
                          item.isActive
                            ? 'text-accent-success hover:bg-accent-success/10'
                            : 'text-text-muted hover:bg-surface-subtle'
                        }`}
                        title={item.isActive ? 'Деактивировать' : 'Активировать'}
                      >
                        {item.isActive ? (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                        ) : (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                        )}
                      </button>
                      <button
                        onClick={() => setEditing(item)}
                        className="p-1.5 rounded-comp text-text-secondary hover:bg-surface-subtle transition-colors"
                        title="Редактировать"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                      </button>
                      {deleteConfirm === item.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="px-2 py-1 text-xs bg-accent-danger text-white rounded-comp hover:bg-accent-danger/90"
                          >
                            Удалить
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-2 py-1 text-xs text-text-secondary hover:bg-surface-subtle rounded-comp"
                          >
                            Отмена
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(item.id)}
                          className="p-1.5 rounded-comp text-text-secondary hover:text-accent-danger hover:bg-accent-danger/10 transition-colors"
                          title="Удалить"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
                    <span>v{item.version}</span>
                    <span>{new Date(item.updatedAt).toLocaleDateString('ru-RU')}</span>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function getItemsLabel(count: number): string {
  if (count === 0) return 'записей';
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 19) return 'записей';
  if (mod10 === 1) return 'запись';
  if (mod10 >= 2 && mod10 <= 4) return 'записи';
  return 'записей';
}
